import React, { useEffect, useId, useRef, useState } from 'react';
import { searchFoods, FoodItem } from '../../services/foodDatabaseService';
import { Button } from '../ui/Button';

interface FoodAutocompleteInputProps {
    value: string;
    onChange: (value: string) => void;
    onNutrientChange?: (nutrients: { calories: number; protein: number; carbs: number; fats: number }, text: string) => void;
    placeholder?: string;
    className?: string;
}

export const FoodAutocompleteInput: React.FC<FoodAutocompleteInputProps> = ({
    value,
    onChange,
    onNutrientChange,
    placeholder,
    className
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [focused, setFocused] = useState(false);
    const [suggestions, setSuggestions] = useState<FoodItem[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [selectedFood, setSelectedFood] = useState<FoodItem | null>(null);
    const [quantity, setQuantity] = useState('');
    const [showQuantityInput, setShowQuantityInput] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const suppressNextSearch = useRef(false);
    const listboxId = `food-suggestions-${useId().replace(/:/g, '')}`;

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
                setFocused(false);
                if (showQuantityInput) {
                    setShowQuantityInput(false);
                    setSelectedFood(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showQuantityInput]);

    useEffect(() => {
        let active = true;
        const term = inputValue.trim();

        if (suppressNextSearch.current) {
            suppressNextSearch.current = false;
            setSuggestions([]);
            setShowSuggestions(false);
            setSearchError('');
            return () => { active = false; };
        }

        if (!focused || term.length < 2 || selectedFood || showQuantityInput) {
            setSuggestions([]);
            setShowSuggestions(false);
            setLoading(false);
            setSearchError('');
            return () => { active = false; };
        }

        setLoading(true);
        setSearchError('');
        const timer = window.setTimeout(async () => {
            try {
                const results = await searchFoods(term, undefined, 10);
                if (!active) return;
                setSuggestions(results);
                setShowSuggestions(true);
            } catch (error) {
                if (!active) return;
                setSuggestions([]);
                setSearchError(error instanceof Error
                    ? error.message
                    : 'Não foi possível consultar a tabela de alimentos.');
                setShowSuggestions(true);
            } finally {
                if (active) setLoading(false);
            }
        }, 350);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [inputValue, selectedFood, showQuantityInput, focused]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setInputValue(nextValue);
        onChange(nextValue);
        setSelectedFood(null);
        setShowQuantityInput(false);
    };

    const handleSelectFood = (food: FoodItem) => {
        setSelectedFood(food);
        setQuantity(String(food.servingSize));
        setShowSuggestions(false);
        setShowQuantityInput(true);
    };

    const handleConfirmQuantity = () => {
        if (!selectedFood) return;

        const quantityValue = Number(quantity);
        if (!Number.isFinite(quantityValue) || quantityValue <= 0 || selectedFood.servingSize <= 0) return;

        const finalText = `${selectedFood.name} (${quantityValue}${selectedFood.servingUnit})`;
        suppressNextSearch.current = true;
        setInputValue(finalText);
        onChange(finalText);

        if (onNutrientChange) {
            const multiplier = quantityValue / selectedFood.servingSize;
            onNutrientChange({
                calories: Number(selectedFood.nutritionalInfo.calories) * multiplier,
                protein: Number(selectedFood.nutritionalInfo.protein) * multiplier,
                carbs: Number(selectedFood.nutritionalInfo.carbs) * multiplier,
                fats: Number(selectedFood.nutritionalInfo.fats) * multiplier
            }, finalText);
        }

        setShowQuantityInput(false);
        setSelectedFood(null);
    };

    const quantityValue = Number(quantity);
    const validQuantity = Number.isFinite(quantityValue) && quantityValue > 0;

    return (
        <div className="ns-food-autocomplete" ref={wrapperRef}>
            <input
                type="text"
                className={className}
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => setFocused(true)}
                onBlur={(event) => {
                    if (!wrapperRef.current?.contains(event.relatedTarget as Node)) {
                        setFocused(false);
                        setShowSuggestions(false);
                    }
                }}
                autoComplete="off"
                role="combobox"
                aria-label={placeholder || 'Buscar alimento'}
                aria-autocomplete="list"
                aria-expanded={showSuggestions}
                aria-controls={listboxId}
            />

            {showSuggestions && (
                <div id={listboxId} className="ns-food-suggestions" role="listbox" aria-label="Alimentos encontrados">
                    {loading && <div className="ns-food-search-status" role="status">Buscando na tabela de alimentos...</div>}
                    {!loading && searchError && <div className="ns-food-search-status ns-food-search-error" role="alert">{searchError}</div>}
                    {!loading && !searchError && suggestions.length === 0 && (
                        <div className="ns-food-search-status" role="status">Nenhum alimento encontrado para esta busca.</div>
                    )}
                    {!loading && suggestions.map((food) => (
                        <button
                            key={food.id}
                            type="button"
                            className="ns-food-suggestion"
                            role="option"
                            aria-selected="false"
                            onClick={() => handleSelectFood(food)}
                        >
                            <span className="ns-food-suggestion-name">{food.name}</span>
                            <span className="ns-food-suggestion-meta">
                                {food.servingSize}{food.servingUnit} · {food.nutritionalInfo.calories} kcal
                                {food.testFixture ? ' · DEMONSTRAÇÃO' : food.fromTaco ? ' · TACO' : ''}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {showQuantityInput && selectedFood && (
                <div className="ns-food-quantity" role="group" aria-label={`Quantidade de ${selectedFood.name}`}>
                    <div className="ns-food-quantity-title">{selectedFood.name}</div>
                    <label className="ns-food-quantity-row">
                        <span>Quantidade</span>
                        <input
                            type="number"
                            min="0.1"
                            step="0.1"
                            value={quantity}
                            onChange={(event) => setQuantity(event.target.value)}
                            className="ns-input"
                            autoFocus
                        />
                        <span className="ns-food-quantity-unit">{selectedFood.servingUnit}</span>
                    </label>

                    <div className="ns-food-nutrition-preview">
                        {(() => {
                            const multiplier = validQuantity ? quantityValue / selectedFood.servingSize : 0;
                            return (
                                <>
                                    🔥 {(selectedFood.nutritionalInfo.calories * multiplier).toFixed(0)} kcal<br />
                                    🥩 P: {(selectedFood.nutritionalInfo.protein * multiplier).toFixed(1)}g{' '}
                                    | 🌾 C: {(selectedFood.nutritionalInfo.carbs * multiplier).toFixed(1)}g{' '}
                                    | 🥑 G: {(selectedFood.nutritionalInfo.fats * multiplier).toFixed(1)}g
                                </>
                            );
                        })()}
                    </div>

                    <div className="ns-food-quantity-actions">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowQuantityInput(false);
                                setSelectedFood(null);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button onClick={handleConfirmQuantity} disabled={!validQuantity}>
                            Confirmar
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
